import { useMemo } from 'react';
import {
  assignStepNumbers,
  collectStepInputPredecessors,
  toStepNumberBridge,
  validateDraftForPublish,
  type ProcessWithSteps
} from '@bliks-io/contracts';
import {
  makeEdgeKey,
  type EdgeKey,
  type EdgeWaypoint,
  type NodeClassifierContext,
  type NodePositionMap
} from '@bliks-io/ui';
import { useProcessDraft } from '@/api/queries/useProcessDraft';
import { useProcess } from '@/api/queries/useProcesses';
import { useProcessLayout } from '@/api/queries/useProcessLayout';
import { draftStepToDisplay } from './adapters/draftStepToDisplay';
import { draftAssignmentToDisplay } from './adapters/draftAssignmentToDisplay';
import { buildPlaceholderAssignments } from './adapters/placeholderAssignments';
import { draftEditorEventsOf } from './draftEvents';

export function useDraftEditorData(processId: string) {
  const draftQuery = useProcessDraft(processId);
  const processQuery = useProcess(processId);
  const layoutQuery = useProcessLayout(processId, 'draft');

  const publishedRecording = processQuery.data?.process?.recording;

  const draftData = draftQuery.data && draftQuery.data !== 'not-found' ? draftQuery.data : null;

  const steps = useMemo(() => draftData?.steps ?? [], [draftData]);
  const draftAssignments = useMemo(() => draftData?.draftAssignments ?? [], [draftData]);
  // Canonical draft event ROWS, keyed by `stableStepId` (see draftEvents.ts).
  // They carry `eventKind`, which activates the flow builder's unified node
  // loop: the start event renders as a real node under its stable id.
  const draftEvents = useMemo(() => draftEditorEventsOf(draftData), [draftData]);

  // Raw-id node classification context for edge actions / context menus —
  // event node ids are bare stable ids, indistinguishable from step ids
  // without this map.
  const classifyContext = useMemo<NodeClassifierContext>(
    () => ({
      eventKindByNodeId: new Map(draftEvents.map((ev) => [ev._id, ev.eventKind]))
    }),
    [draftEvents]
  );

  // Every suggestion (any status) referenced by a `suggestion`-typed ref across
  // the draft, fetched alongside the draft. Threaded into the display adapters
  // so the detail panel resolves proposed Team/System names, and passed to the
  // editor canvas (which derives its own proposed-name/status map). This is the
  // draft-wide union set, not a per-step slice (recordings denormalize per-step
  // `entitySuggestions[]`; drafts don't) — every consumer resolves by id, so the
  // extra entries handed to each adapter are inert.
  const entitySuggestions = useMemo(() => draftData?.entitySuggestions ?? [], [draftData]);

  // The publish gate's own rules, run live: the issues sidebar lists exactly
  // the errors Publish would return, and Publish stays disabled while any remain.
  const validation = useMemo(
    () => validateDraftForPublish(steps, draftAssignments, draftEvents),
    [steps, draftAssignments, draftEvents]
  );

  const layoutData = layoutQuery.data;

  // Derived from the layout cache (keyed by `stableStepId`, matching the new
  // node.id). Missing entries mean "unpositioned" — `useEditableFlow` (via
  // `seedAlgoOnReady`) then runs ELK once on first mount to seed positions.
  const nodePositions = useMemo(() => {
    const map: NodePositionMap = new Map();
    for (const p of layoutData?.stepPositions ?? []) {
      map.set(String(p.stableStepId), { x: p.x, y: p.y });
    }
    for (const generated of layoutData?.generatedNodePositions ?? []) {
      map.set(generated.nodeId, { x: generated.x, y: generated.y });
    }
    return map;
  }, [layoutData]);

  // Unique 1…N step number per real step, in flow order (BFS depth, ties on
  // stableStepId). Uses the SAME shared `assignStepNumbers` as the backend
  // recording view, numbering off each step's COMPLETE predecessor set (all
  // `inputs[]` edges via `collectStepInputPredecessors`) and the same shared
  // `toStepNumberBridge` for placeholder anchoring, so the canvas badge matches
  // the detail-page table for a given process — before and after publish.
  const stepNumberByStableId = useMemo(
    () =>
      assignStepNumbers({
        steps: steps.map((s) => ({
          id: String(s.stableStepId),
          predecessors: collectStepInputPredecessors(s.inputs)
        })),
        startKeys: draftEvents.filter((ev) => ev.eventKind === 'start').map((ev) => ev._id),
        bridges: draftAssignments.map((a) =>
          toStepNumberBridge(String(a.stableStepId), collectStepInputPredecessors(a.inputs))
        )
      }),
    [steps, draftEvents, draftAssignments]
  );

  // Flow builder input. Draft assignments are intentionally excluded: they
  // already render as placeholders via `openAssignments`, and duplicating
  // them here produces two edge paths per stableStepId (different source
  // handles when a gateway is upstream), plus cascading deletes that wipe
  // live assignments.
  const recordedStepsForFlow = useMemo(
    () =>
      steps.map((s) => {
        const display = draftStepToDisplay(s, processId, entitySuggestions);
        return { ...display, stepNumber: stepNumberByStableId.get(display._id) };
      }),
    [steps, processId, entitySuggestions, stepNumberByStableId]
  );

  // Merged projection for the side-panel's sibling list only — not the
  // flow builder.
  const displayedSteps = useMemo(
    () => [
      ...recordedStepsForFlow,
      ...draftAssignments.map((a) => draftAssignmentToDisplay(a, processId, entitySuggestions))
    ],
    [recordedStepsForFlow, draftAssignments, processId, entitySuggestions]
  );

  // Live `openAssignments` are intentionally not merged in: new invitations
  // that spawn after draft creation only become visible on discard-and-
  // recreate. Status for each placeholder is read from the draft row's
  // frozen `statusSnapshot`.
  const effectiveOpenAssignments = useMemo(
    () => buildPlaceholderAssignments(draftAssignments),
    [draftAssignments]
  );

  const processForFlow = useMemo<ProcessWithSteps | null>(() => {
    if (!draftData) return null;
    return {
      _id: processId,
      processStepRecordings: recordedStepsForFlow,
      recording: publishedRecording,
      updatedAt:
        draftData.draft.updatedAt instanceof Date
          ? draftData.draft.updatedAt.toISOString()
          : String(draftData.draft.updatedAt ?? '')
    } as unknown as ProcessWithSteps;
  }, [draftData, processId, recordedStepsForFlow, publishedRecording]);

  // Saved edge waypoints from the same layout cache. Identity-compared by
  // `useEditableFlow` — derived via `useMemo` so re-renders on unrelated
  // state don't rebuild the map and re-seed the kit's manager.
  const edgeWaypoints = useMemo(() => {
    const map = new Map<EdgeKey, EdgeWaypoint[]>();
    for (const e of layoutData?.edgeLayouts ?? []) {
      const key = makeEdgeKey(e.sourceNodeId, e.targetNodeId, e.sourceHandleId ?? null);
      map.set(
        key,
        e.waypoints.map((w) => ({ id: w.id, x: w.x, y: w.y }))
      );
    }
    return map;
  }, [layoutData]);

  return {
    draftQuery,
    layoutQuery,
    layoutData,
    publishedRecording,
    draftData,
    steps,
    draftAssignments,
    draftEvents,
    classifyContext,
    validation,
    stepNumberByStableId,
    recordedStepsForFlow,
    displayedSteps,
    effectiveOpenAssignments,
    entitySuggestions,
    processForFlow,
    nodePositions,
    edgeWaypoints
  };
}
